import { getInitials } from '@utils/Format'
import { isParentTask } from '@modules/tarefas/utils/isOverdue'
import { FINAL_STATUS_CATEGORIES } from '@modules/tarefas/constants/Text'
import { decoratePeople } from '@modules/tarefas/utils/personDecoration'
import { resolveProfissionalId } from '@modules/tarefas/utils/ownership'
import { _statusMapById, _loadedStatuses, _taskCategory } from './getters-internal'

// --- Quick-view (visões rápidas) slice ---------------------------------
// `activeQuickView` é a chave da visão ativa (ou null em modo sprint).
// `quickViewCounts` é o mapa de contadores reais do backend. `isQuickViewMode`
// deriva a flag booleana usada pelo board para guardar a máquina de empty-state.
// Nota: `currentSprintTasks` já retorna `state.tasks` inteiro quando não há
// `activeSprintId`. Como o board virou data-driven por status (por pasta), a
// list-view NÃO renderiza a visão rápida agrupando por status da pasta ativa
// (inexistente em cross-pasta → derrubaria toda tarefa): `listGroups` detecta
// `isQuickViewMode` e agrupa pela taxonomia fixa de CATEGORIA, resolvida do
// `statusCategory` embutido no payload da quick-view (ver `_quickViewListGroups`).

// --- Slice cru de tarefas -----------------------------------------------
// As tarefas já carregadas no store, sem filtro nem poda — é o payload como o
// `SET_TASKS`/`APPEND_TASKS` deixou. Existe para quem precisa observar a
// CHEGADA do payload (a list-view usa como fonte do watcher de hidratação dos
// custom fields); para renderizar, use `currentSprintTasks`/`listGroups`, que
// aplicam filtro e agrupamento. Default `[]`, nunca `undefined`.
export function loadedTasks(state) {
  return state.tasks || []
}

export function activeQuickView(state) {
  return state.activeQuickView
}

export function quickViewCounts(state) {
  return state.quickViewCounts || {}
}

export function isQuickViewMode(state) {
  return !!state.activeQuickView
}

export function quickViewPagination(state) {
  return state.quickViewPagination || { page: 1, pageSize: 0, total: 0 }
}

/**
 * Ainda há página por buscar no servidor?
 *
 * Compara o total de LINHAS-RAIZ do conjunto com quantas raízes já estão na
 * store — não com `page * pageSize`, que mentiria se uma página voltasse menor
 * (dedup do `APPEND_TASKS`, ou tarefa removida entre duas páginas).
 */
export function hasMoreQuickViewPages(state) {
  if (!state.activeQuickView) return false
  const total = (state.quickViewPagination || {}).total || 0
  const loadedRoots = (state.tasks || []).length
  return loadedRoots < total
}

// --- Progresso da lista ativa (referencia.png) -------------------------
// Cabeçalho do board exibe "Progresso X/Y" + avatares dos responsáveis da
// sprint ativa. Contamos apenas tarefas-pai (parentTasks) para refletir o
// que aparece na lista — sub-tarefas não entram na contagem do progresso.

function _normalizePerson(person) {
  if (!person || person.id == null) return null
  const name = person.nome || person.name || ''
  const initials = person.initials || getInitials(name)
  return {
    id: person.id,
    tipo: person.tipo || 'profissional',
    name: name,
    initials: initials,
    avatarUrl: person.avatarUrl || person.avatar_url || null,
  }
}

// Fonte de status para resolver a CATEGORIA de uma tarefa: a fatia da pasta
// ativa em modo sprint, ou o conjunto achatado de todas as fatias carregadas em
// modo visão rápida (cross-pasta, onde não há pasta ativa). Mesma escolha que
// `listGroups` faz entre `bucketTasksByCategory` e `_quickViewGroups`.
function _categorySourceStatuses(state, getters) {
  if (getters.isQuickViewMode) return _loadedStatuses(state)
  return getters.activeFolderStatuses
}

// Progresso = tarefas-raiz em categoria TERMINAL (`done` + `closed`) sobre o
// total de tarefas-raiz da lista ativa.
//
// Resolver por categoria, e não pelo literal `task.status === 'done'`, é o que
// faz o número funcionar depois de `customizar-status-tarefas`: status viraram
// registros por pasta e as tarefas passaram a carregar `statusId` (UUID), não
// mais o literal legado — que ficava sempre falso e travava o header em 0/N.
// `_taskCategory` mantém os dois fallbacks que o resto do módulo depende: o
// `statusCategory` embutido no payload das visões rápidas e o literal legado
// das linhas pré-migração (`src/modules/solicitacoes/` injeta tarefas na store
// sem passar pelo endpoint deste módulo).
export function currentSprintProgress(state, getters) {
  const tasks = (getters.currentSprintTasks || []).filter(isParentTask)
  const total = tasks.length
  // Uma construção do índice por avaliação — resolver por tarefa reconstruiria
  // o mapa N vezes num getter que recomputa a cada tecla digitada na busca.
  const statusMap = _statusMapById(_categorySourceStatuses(state, getters))
  let done = 0
  for (const task of tasks) {
    if (FINAL_STATUS_CATEGORIES[_taskCategory(task, statusMap)] === true) done++
  }
  const percent = total > 0 ? Math.round((done / total) * 100) : 0
  return { done: done, total: total, percent: percent }
}

export function currentSprintResponsaveis(state, getters) {
  const tasks = (getters.currentSprintTasks || []).filter(isParentTask)
  const byKey = {}
  const order = []
  function add(person) {
    const normalized = _normalizePerson(person)
    if (!normalized) return
    const key = normalized.tipo + ':' + normalized.id
    if (byKey[key]) return
    byKey[key] = normalized
    order.push(key)
  }
  for (const task of tasks) {
    if (!task) continue
    add(task.responsavelPrincipal)
    if (Array.isArray(task.assignees)) task.assignees.forEach(add)
    if (Array.isArray(task.participantes)) task.participantes.forEach(add)
  }
  return order.map(function (key) {
    return byKey[key]
  })
}

// --- Overview slice (server-aggregated) --------------------------------
// All four panels in the Overview tab read pre-aggregated payloads from
// the backend. Nothing here iterates `state.tasks`. Defensive defaults
// guarantee consumers never crash on a cold/missing slice.

export function overviewOverdueTasks(state) {
  return (state.overview && state.overview.overdueTasks) || []
}

export function overviewWeeklyDue(state) {
  return (state.overview && state.overview.weeklyDue) || []
}

export function overviewWeeklyDone(state) {
  return (state.overview && state.overview.weeklyDone) || []
}

export function overviewMemberDistribution(state) {
  return (state.overview && state.overview.memberDistribution) || { members: [], hasOverdue: false }
}

export function overviewClientDistribution(state) {
  return (state.overview && state.overview.clientDistribution) || { members: [], hasOverdue: false }
}

export function overviewChartMonthly(state) {
  return (state.overview && state.overview.chartMonthly) || { categories: [], created: [], resolved: [] }
}

export function overviewLoading(state) {
  return (state.overview && state.overview.loading) || {
    overdueTasks: false,
    weeklyDue: false,
    weeklyDone: false,
    memberDistribution: false,
    clientDistribution: false,
    chartMonthly: false,
  }
}

// Varre TODAS as pastas (raiz + subpastas, via `allFolders`): a lista ativa
// pode morar dentro de uma subpasta, e uma varredura só das raízes devolveria
// null — o cabeçalho do board perderia o nome da lista selecionada.
export function activeSprint(state, getters) {
  if (!state.activeSprintId) return null
  for (const folder of getters.allFolders) {
    if (!Array.isArray(folder.sprints)) continue
    for (const sprint of folder.sprints) {
      if (sprint && sprint.id === state.activeSprintId) return sprint
    }
  }
  return null
}

export function assignees(state) {
  return state.assignees || { professionals: [], clients: [] }
}

export function assigneesProfessionals(state) {
  return (state.assignees && state.assignees.professionals) || []
}

// ★ A regra do "logado em primeiro" — implementação única.
//
// Hoist, não reordenação: a ordem relativa de todos os outros é preservada.
// "Minhas tarefas" é o recorte dominante do módulo — o próprio
// `resolveAutoFilledResponsavel` já assume "eu" como default ao criar — então
// ter que caçar o próprio nome numa lista de dezenas de pessoas é atrito puro.
// Com a janela de renderização do picker (`visibleProfessionals`), estar no topo
// é também o que garante que o logado apareça sem crescer a janela.
//
// A identidade da sessão vem de `resolveProfissionalId` (`utils/ownership.js`),
// não de `user.id_profissional` cru: o payload de login expõe o id em
// `id_interno` na maior parte dos casos, e esse helper é o ponto único que já
// concilia os dois shapes. O `.id` das duas listas (crua e decorada) vive no
// mesmo espaço — é o `id_profissional` que `addAssignee` posta.
//
// Devolve a MESMA referência da lista de entrada quando não há nada a mover
// (logado ausente, já em primeiro, ou sessão sem profissional) — sem isso, cada
// consumidor re-renderizaria a cada avaliação. Quando há hoist, devolve array
// novo com os MESMOS itens: nunca ordena in-place (o array cru é o array vivo
// do Vuex) e nunca clona pessoa (o contrato de `assigneesDecorated*` é que os
// pickers compartilhem as mesmas referências de item — ver `personDecoration`).
function _hoistSelfFirst(list, rootGetters) {
  if (!Array.isArray(list) || list.length < 2) return list || []
  const currentUser = rootGetters && rootGetters['user/currentUser']
  const selfId = resolveProfissionalId(currentUser)
  if (selfId == null) return list
  const selfIndex = list.findIndex(function (person) {
    return person && person.id === selfId
  })
  // -1 = não está na lista; 0 = já é o primeiro. Nos dois casos, nada a fazer.
  if (selfIndex <= 0) return list
  const reordered = list.slice()
  reordered.splice(selfIndex, 1)
  reordered.unshift(list[selfIndex])
  return reordered
}

// Lista CRUA com o logado em primeiro — consumida pelo filtro "Responsável" do
// Board (`constants/filtros.js`), que mapeia para `{ id, value }` e não precisa
// da decoração.
export function assigneesProfessionalsSelfFirst(state, getters, rootState, rootGetters) {
  return _hoistSelfFirst(assigneesProfessionals(state), rootGetters)
}

export function assigneesClients(state) {
  return (state.assignees && state.assignees.clients) || []
}

/**
 * Listas decoradas UMA vez para toda a aplicação.
 *
 * A Lista de Tarefas monta 2 `TaskAssigneePicker` + 1 `TaskClientPicker` por
 * linha. Enquanto cada picker decorava a própria lista, o custo era
 * `linhas × pickers × pessoas` — 197 profissionais + 689 clientes no escritório
 * 83. Aqui é `pessoas`, uma vez, e os pickers filtram sobre estas MESMAS
 * referências (busca e `excludePerson` devolvem subconjuntos, não cópias).
 *
 * Só o que é invariante por pessoa vive aqui. `selected` e `marked` dependem da
 * tarefa da linha e continuam no componente, como mapas indexados por
 * `key`/`id` — ver `utils/personDecoration.js`.
 */
export function assigneesDecoratedProfessionals(state, getters) {
  return decoratePeople(getters.assigneesProfessionals, 'profissional')
}

// Lista DECORADA com o logado em primeiro — é esta que o `TaskAssigneePicker`
// consome. Hoistar sobre a decorada (e não decorar sobre a hoistada) preserva o
// contrato de compartilhamento acima: os itens continuam sendo as MESMAS
// referências de `assigneesDecoratedProfessionals`, só o array externo é novo.
export function assigneesDecoratedProfessionalsSelfFirst(state, getters, rootState, rootGetters) {
  return _hoistSelfFirst(getters.assigneesDecoratedProfessionals, rootGetters)
}

export function assigneesDecoratedClients(state, getters) {
  return decoratePeople(getters.assigneesClients, 'cliente')
}

export function assigneesLastUpdate(state) {
  return state.assigneesLastUpdate || null
}

export function assigneesLoading(state) {
  return state.assigneesLoading || false
}

export function tags(state) {
  return state.tags || []
}

export function activeTags(state) {
  return (state.tags || []).filter(function (t) {
    return t && t.active
  })
}

export function tagsLastUpdate(state) {
  return state.tagsLastUpdate || null
}

// Higher-order getter: `store.getters['tarefas/tagById'](id)` returns the tag
// object (or undefined). Convenient for resolving optimistic chip rendering
// when the picker only has the id.
export function tagById(state) {
  return function (id) {
    return (state.tags || []).find(function (t) {
      return t && t.id === id
    })
  }
}
